import { lazy, Suspense } from "react";
import { Loader } from "@/components";
import QuoteLinePage from "@/pages/QuoteLine.page";
import CustomerQuotePage from "@/pages/CustomerPages/CustomerQuote.page";
import VendorQuotePage from "@/pages/VendorsPages/VendorQuote.page";
import AIRXSingleQuoteLinePage from "@/pages/AIRXPages/AIRXSingleQuoteLine.page";
import TableFiltersContextProvider from "@/common/contexts/Table.context";
import QuoteProvider from "@/common/contexts/QuoteResults.context";
import { redirect } from "react-router";
import { guardCheck } from "@/common/utils";
import { APIProvider } from "@vis.gl/react-google-maps";
import { libs } from "@/common/const";
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

const SingleManualQuotePage = lazy(
  () => import("@/pages/SingleManualQuote.page")
);
const SingleSiteResults = lazy(() => import("@/pages/SingleSiteResults.page"));
const MultiSiteQuoteResults = lazy(
  () => import("@/pages/MultiSiteQuoteResults.page")
);
const Quotes = lazy(() => import("@/pages/AIRXPages/Quotes.page"));
const VendorSingleQuoteLinePage = lazy(
  () => import("@/pages/VendorsPages/VendorSingleQuoteLine.page")
);
const CustomerSingleQuoteLinePage = lazy(
  () => import("@/pages/CustomerPages/CustomerSingleQuoteLine.page")
);
const MultiManualQuote = lazy(() => import("@/pages/MultiManualQuote.page"));
const AlternativeFlowPage = lazy(() => import("@/pages/AlternativeFlow.page"));
const quoteRoute = [
  {
    path: "singleSiteQuoteResults/:quoteResultId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(
        request,
        "singleSiteQuoteResults/airx/customer"
      );
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <TableFiltersContextProvider>
            <QuoteProvider>
              <SingleSiteResults />
            </QuoteProvider>
          </TableFiltersContextProvider>
        </Suspense>
      </APIProvider>
    ),
  },
  {
    path: "manualFlow",
    element: (
      <Suspense fallback={<Loader />}>
        <AlternativeFlowPage />
      </Suspense>
    ),
  },
  {
    path: "multiSiteResults/:multiQuoteId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "multiSiteResults/airx/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense>
        <TableFiltersContextProvider>
          <QuoteProvider>
            <MultiSiteQuoteResults />
          </QuoteProvider>
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "multiSiteManual",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "multiSiteManual/airx/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense>
        <MultiManualQuote />
      </Suspense>
    ),
  },

  {
    path: "quotes",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quotes/arix");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <Quotes />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "vendor/quotes/me",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quotes/vendor");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <VendorQuotePage />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "customer/quotes/me",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quotes/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <Suspense fallback={<Loader />}>
        <TableFiltersContextProvider>
          <CustomerQuotePage />
        </TableFiltersContextProvider>
      </Suspense>
    ),
  },
  {
    path: "singleManualQuote",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "singleManualQuote/airx/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <SingleManualQuotePage />
        </Suspense>
      </APIProvider>
    ),
  },
  {
    path: "quoteLine/:quoteId/:quoteLineId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quoteLine/airx/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <QuoteLinePage />
        </Suspense>
      </APIProvider>
    ),
  },
  {
    path: "quoteLine/airx/:quoteId/:quoteLineId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quoteLine/arix");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <AIRXSingleQuoteLinePage />
        </Suspense>
      </APIProvider>
    ),
  },
  {
    path: "quoteLine/vendor/:quoteId/:quoteLineId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quoteLine/vendor");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <VendorSingleQuoteLinePage />
        </Suspense>
      </APIProvider>
    ),
  },
  {
    path: "quoteLine/customer/:quoteId/:quoteLineId",
    loader: async ({ request }: { request: Request }) => {
      const isAllowed = guardCheck(request, "quoteLine/customer");
      if (!isAllowed) {
        throw redirect("/404");
      }
      return request;
    },
    element: (
      <APIProvider apiKey={GOOGLE_API_KEY} libraries={[...libs]} language="en">
        <Suspense fallback={<Loader />}>
          <CustomerSingleQuoteLinePage />
        </Suspense>
      </APIProvider>
    ),
  },
];

export default quoteRoute;
